import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  InputAdornment,
  Chip,
  Avatar,
  Menu,
  MenuItem,
  Alert,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Autocomplete,
  Snackbar,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Search,
  MoreVert,
  Edit,
  Block,
  CheckCircleOutline,
  Person,
  Phone,
  Class,
  Add,
} from '@mui/icons-material';
import { colors } from '../theme/theme';
import { apiService } from '../services/apiService';
import { useAuth } from '../contexts/AuthContext';
import InstructorFormDialog from '../components/InstructorFormDialog';
import InstructorHandoverFields from '../components/instructor/InstructorHandoverFields';
import { isOwnerRole } from '../utils/roles';
import BranchChip from '../components/BranchChip';
import { useMyLocations } from '../hooks/useMyLocations';
import { actingStudioOf, splitInstructorLists } from '../utils/studioOf';

interface Instructor {
  /** Every location this person teaches at (populated with studioName). */
  studios?: Array<{ _id: string; studioName?: string } | string>;
  _id: string;
  name: string;
  email?: string;
  phoneNumber: string;
  image?: string;
  gender: 'Male' | 'Female' | 'Other';
  description?: string;
  studio: {
    _id: string;
    studioName: string;
  };
  isDisabled?: boolean;
  createdAt: string;
  updatedAt: string;
}

const InstructorsPage: React.FC = () => {
  const navigate = useNavigate();
  const { admin } = useAuth();
  const studioId = (admin as any)?.studio?._id || '';
  const { isAllLocations, locations, roleByStudio, selectedLocationId: selectedLocationIdForWrites } = useMyLocations();
  const locationById = new Map<string, any>(locations.map((l: any) => [String(l._id), l]));

  const [instructors, setInstructors] = useState<Instructor[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedInstructor, setSelectedInstructor] = useState<Instructor | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [instructorToEdit, setInstructorToEdit] = useState<Instructor | null>(null);

  // Studio owners + managers can create instructors for their own studio
  // (backend StudioManagerOrAdminGuard). Super-admins manage instructors per
  // studio elsewhere, and the global view has no single studio context.
  const isSuperAdmin = admin?.role === 'super_admin' || admin?.role === 'admin';
  const canManageInstructors =
    isSuperAdmin || (!!studioId && (isOwnerRole(admin?.role) || admin?.role === 'manager'));

  // Super admin has no studio of their own: they first choose which studio
  // the instructor is created at; the form then offers that brand's locations.
  const [pickStudioOpen, setPickStudioOpen] = useState(false);
  const [studioOptions, setStudioOptions] = useState<Array<{ _id: string; studioName: string; branchName?: string; city?: string }>>([]);
  const [studiosLoading, setStudiosLoading] = useState(false);
  const [studiosError, setStudiosError] = useState<string | null>(null);
  const [pickedStudio, setPickedStudio] = useState<{ _id: string; studioName: string } | null>(null);
  const [createForStudio, setCreateForStudio] = useState<string>('');
  const openCreate = async () => {
    if (!isSuperAdmin) {
      setCreateForStudio(studioId);
      setCreateDialogOpen(true);
      return;
    }
    setPickedStudio(null);
    setStudiosError(null);
    setPickStudioOpen(true);
    if (studioOptions.length) return;
    setStudiosLoading(true);
    try {
      const res: any = await apiService.getStudios();
      const list = res?.data ?? res?.studios ?? [];
      setStudioOptions(
        (Array.isArray(list) ? list : [])
          .filter((st: any) => st?._id)
          .sort((a: any, b: any) => String(a.studioName).localeCompare(String(b.studioName))),
      );
    } catch (e: any) {
      setStudiosError(e?.response?.data?.message || e?.message || 'Could not load studios.');
    } finally {
      setStudiosLoading(false);
    }
  };
  const [toggleDialogOpen, setToggleDialogOpen] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  // Active | Deactivated (access revoked at this location — reactivate from here)
  const [listTab, setListTab] = useState<'active' | 'removed'>('active');
  const [removedInstructors, setRemovedInstructors] = useState<any[]>([]);
  const [reactivateTarget, setReactivateTarget] = useState<any | null>(null);
  const [reactivating, setReactivating] = useState(false);
  const [reactivateError, setReactivateError] = useState<string | null>(null);
  const [instructorToToggle, setInstructorToToggle] = useState<Instructor | null>(null);
  const [isToggling, setIsToggling] = useState(false);
  // Set when the backend refuses a removal because the instructor is still on
  // the schedule. Its presence is what turns the dialog into a handover.
  const [futureWork, setFutureWork] = useState<{
    sessionCount: number;
    classCount: number;
    eventCount: number;
    wellnessPoolCount: number;
    serviceCount: number;
    bookingCount: number;
    /** Distinct people, not bookings — that is who the notice goes to. */
    memberCount: number;
    nextSessionAt: string | null;
  } | null>(null);
  const [replacementId, setReplacementId] = useState('');
  // Opt-IN, unchecked by default: messaging people who already paid is the
  // studio's decision, not a side effect of removing an instructor.
  const [notifyMembers, setNotifyMembers] = useState(false);

  useEffect(() => {
    fetchInstructors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchInstructors = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const [active, removed]: any[] = await Promise.all([
        apiService.getInstructors(studioId, { status: 'active' }),
        apiService.getInstructors(studioId, { status: 'removed' }).catch(() => null),
      ]);
      const split = splitInstructorLists(
        active?.instructors || active?.data || [],
        removed?.instructors || removed?.data || [],
      );
      setInstructors(split.active);
      setRemovedInstructors(split.deactivated);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch instructors');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, instructor: Instructor) => {
    setAnchorEl(event.currentTarget);
    setSelectedInstructor(instructor);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedInstructor(null);
  };

  const handleEditInstructor = () => {
    if (selectedInstructor) {
      setInstructorToEdit(selectedInstructor);
      setEditDialogOpen(true);
    }
    handleMenuClose();
  };

  const handleToggleInstructor = () => {
    if (selectedInstructor) {
      setInstructorToToggle(selectedInstructor);
      setToggleDialogOpen(true);
    }
    handleMenuClose();
  };

  /** Where a reactivation is for: the location they were removed from that this user manages. */
  const reactivateAt = (ins: any): string | undefined => {
    if (isSuperAdmin) return undefined; // super admin: every location they were removed from
    const removedIds: string[] = (ins?.removedFrom ?? []).map((r: any) => String(r.studio?._id ?? r.studio));
    const mine = locations.map((l) => String(l._id));
    return (
      (selectedLocationIdForWrites && removedIds.includes(selectedLocationIdForWrites) ? selectedLocationIdForWrites : undefined) ||
      removedIds.find((id) => mine.includes(id)) ||
      removedIds[0] ||
      studioId ||
      undefined
    );
  };

  const confirmReactivate = async () => {
    if (!reactivateTarget) return;
    setReactivating(true);
    setReactivateError(null);
    try {
      const at = reactivateAt(reactivateTarget);
      const res: any = await apiService.setInstructorDisabled(reactivateTarget._id, false, undefined, undefined, at);
      const out = res?.data ?? res;
      const names = (out?.reactivatedAt ?? []).map(
        (id: string) => locationById.get(String(id))?.branchName || locationById.get(String(id))?.studioName,
      ).filter(Boolean);
      setNotice(`${reactivateTarget.name} is active again${names.length ? ` at ${names.join(', ')}` : ''}.`);
      setReactivateTarget(null);
      await fetchInstructors();
    } catch (err: any) {
      setReactivateError(err?.response?.data?.message || err?.message || 'Could not reactivate. Please try again.');
    } finally {
      setReactivating(false);
    }
  };

  // The one location a studio user's revoke acts on — named in the dialog,
  // so "All locations" never leaves it ambiguous.
  const revokeLocationId = instructorToToggle && !isSuperAdmin
    ? actingStudioOf(instructorToToggle, {
        isAllLocations,
        myLocationIds: locations.map((l) => String(l._id)),
        roleByStudio,
      })
    : undefined;
  const revokeLocationName =
    (revokeLocationId &&
      (locationById.get(String(revokeLocationId))?.branchName ||
        locationById.get(String(revokeLocationId))?.studioName)) ||
    'this location';

  const confirmToggleInstructor = async () => {
    if (!instructorToToggle) return;
    try {
      setIsToggling(true);
      const res: any = await apiService.setInstructorDisabled(
        instructorToToggle._id,
        !instructorToToggle.isDisabled,
        replacementId || undefined,
        notifyMembers,
        // Pinned: under "All locations" the switcher sends __all__, which the
        // server refuses for writes.
        isSuperAdmin
          ? undefined
          : actingStudioOf(instructorToToggle, {
              isAllLocations,
              myLocationIds: locations.map((l) => String(l._id)),
              roleByStudio,
            }),
      );
      setToggleDialogOpen(false);
      setInstructorToToggle(null);
      setFutureWork(null);
      setReplacementId('');
      setNotifyMembers(false);
      const out = res?.data ?? res;
      const here = out?.removedFromLocation
        ? locationById.get(String(out.removedFromLocation))?.branchName ||
          locationById.get(String(out.removedFromLocation))?.studioName ||
          'this location'
        : 'every location';
      const left = out?.stillTeachesAt?.length ?? 0;
      setNotice(
        `${instructorToToggle.name}'s access was revoked at ${here}.` +
          (left ? ` They still teach at ${left} other location${left === 1 ? '' : 's'}.` : '') +
          ' Find them under Deactivated to reactivate.',
      );
      await fetchInstructors();
    } catch (err: any) {
      // The backend refuses to remove an instructor who still has upcoming
      // work, and returns the counts with the refusal. That is not an error to
      // show — it is the picker's cue.
      // The refusal carries the counts of what still points at this instructor,
      // and the picker is built from them. Read `err.data`: the response
      // interceptor flattens the axios error, so `err.response` is undefined
      // and the old `err?.response?.data` made this branch unreachable — the
      // operator saw "Choose who takes them over" and no control to do it with.
      const data = err?.data ?? err?.response?.data;
      if (data?.code === 'has_future_assignments') {
        setFutureWork({
          sessionCount: data.sessionCount ?? 0,
          classCount: data.classCount ?? 0,
          eventCount: data.eventCount ?? 0,
          wellnessPoolCount: data.wellnessPoolCount ?? 0,
          serviceCount: data.serviceCount ?? 0,
          bookingCount: data.bookingCount ?? 0,
          memberCount: data.memberCount ?? 0,
          nextSessionAt: data.nextSessionAt ?? null,
        });
        setError(null);
      } else {
        setError(data?.message || err.message || 'Failed to update instructor status');
      }
    } finally {
      setIsToggling(false);
    }
  };

  /** Active instructors at this studio who could take the classes over. */
  const replacementOptions = instructors.filter(
    (i) => i._id !== instructorToToggle?._id && !i.isDisabled,
  );

  const filteredInstructors = instructors.filter(
    (instructor) =>
      instructor.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      instructor.phoneNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      instructor.studio?.studioName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box mb={3}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
          <Box display="flex" alignItems="center" gap={2}>
            <Typography variant="h4" fontWeight={700}>
              Instructors
            </Typography>
            <Chip
              label={`${filteredInstructors.length} Total`}
              size="small"
              sx={{
                backgroundColor: `${colors.primary}15`,
                color: colors.primary,
                fontWeight: 600,
              }}
            />
          </Box>
          {canManageInstructors && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={openCreate}
              sx={{ backgroundColor: colors.primary, '&:hover': { backgroundColor: colors.primary } }}
            >
              Create Instructor
            </Button>
          )}
        </Box>
        <Typography variant="body2" color="text.secondary">
          Manage all instructors across studios
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Search */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ py: 2 }}>
          <TextField
            fullWidth
            placeholder="Search by name, phone, or studio..."
            value={searchTerm}
            onChange={handleSearch}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: colors.textSecondary }} />
                </InputAdornment>
              ),
            }}
          />
        </CardContent>
      </Card>

      {/* Active | Deactivated */}
      <Tabs
        value={listTab}
        onChange={(_, v) => setListTab(v)}
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab value="active" label={`Active (${instructors.length})`} />
        <Tab value="removed" label={`Deactivated (${removedInstructors.length})`} />
      </Tabs>

      {/* Instructors Grid */}
      {listTab === 'removed' ? (
        removedInstructors.length === 0 ? (
          <Card>
            <CardContent sx={{ py: 4, textAlign: 'center' }}>
              <Person sx={{ fontSize: 64, color: colors.textSecondary, mb: 2, opacity: 0.3 }} />
              <Typography variant="h6" fontWeight={600} mb={1}>No deactivated instructors</Typography>
              <Typography variant="body2" color="text.secondary">
                Instructors whose access you revoke appear here, so you can reactivate them later.
              </Typography>
            </CardContent>
          </Card>
        ) : (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }, gap: 3 }}>
            {removedInstructors
              .filter((i: any) => !searchTerm || i.name?.toLowerCase().includes(searchTerm.toLowerCase()))
              .map((ins: any) => {
                const last = [...(ins.removedFrom ?? [])].sort((a: any, b: any) => +new Date(b.at) - +new Date(a.at))[0];
                const where = last?.studio?.branchName || last?.studio?.studioName;
                return (
                  <Card key={ins._id} sx={{ opacity: 0.95 }}>
                    <CardContent sx={{ display: 'grid', gap: 1.2 }}>
                      <Box display="flex" alignItems="center" gap={1.5}>
                        <Avatar sx={{ bgcolor: `${colors.textSecondary}30`, color: colors.textSecondary, fontWeight: 700 }}>
                          {ins.name?.charAt(0) || 'I'}
                        </Avatar>
                        <Box flex={1} minWidth={0}>
                          <Typography fontWeight={600} noWrap>{ins.name}</Typography>
                          <Typography variant="body2" color="text.secondary">{ins.phoneNumber}</Typography>
                        </Box>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        {where ? <>Access revoked at <b>{where}</b></> : 'Deactivated'}
                        {last?.at ? ` · ${new Date(last.at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}` : ''}
                        {last?.by ? ` · by ${last.by}` : ''}
                      </Typography>
                      {(ins.studios?.length ?? 0) > 0 && (
                        <Typography variant="caption" color="text.secondary">
                          Still teaches at {ins.studios.map((st: any) => st?.studioName).filter(Boolean).join(', ')}
                        </Typography>
                      )}
                      {canManageInstructors && (
                        <Button
                          variant="outlined"
                          color="success"
                          startIcon={<CheckCircleOutline />}
                          onClick={() => { setReactivateError(null); setReactivateTarget(ins); }}
                          sx={{ justifySelf: 'start' }}
                        >
                          Reactivate
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
          </Box>
        )
      ) : filteredInstructors.length === 0 ? (
        <Card>
          <CardContent sx={{ py: 4, textAlign: 'center' }}>
            <Person sx={{ fontSize: 64, color: colors.textSecondary, mb: 2, opacity: 0.3 }} />
            <Typography variant="h6" fontWeight={600} mb={1}>
              No Instructors Found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {searchTerm ? 'Try adjusting your search terms' : 'No instructors registered yet'}
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }, gap: 3 }}>
          {filteredInstructors.map((instructor) => (
            <Card
              key={instructor._id}
              onClick={() => navigate(`/instructors/${instructor._id}`)}
              sx={{
                border: `1px solid ${colors.divider}`,
                borderRadius: 2,
                cursor: 'pointer',
                opacity: instructor.isDisabled ? 0.6 : 1,
                transition: 'all 0.2s ease',
                '&:hover': {
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  transform: 'translateY(-4px)',
                  borderColor: colors.secondary,
                },
              }}
            >
              <CardContent>
                <Box display="flex" alignItems="flex-start" gap={2}>
                  <Avatar
                    src={instructor.image}
                    sx={{
                      width: 72,
                      height: 72,
                      backgroundColor: colors.primary,
                      fontSize: '1.5rem',
                      fontWeight: 700,
                    }}
                  >
                    {instructor.name?.charAt(0) || 'I'}
                  </Avatar>
                  <Box flex={1}>
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                      <Typography variant="h6" fontWeight={600}>
                        {instructor.name}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={(e) => { e.stopPropagation(); handleMenuOpen(e, instructor); }}
                      >
                        <MoreVert />
                      </IconButton>
                    </Box>
                    <Box display="flex" alignItems="center" gap={0.5} mb={1.5}>
                      <Chip
                        label={instructor.gender}
                        size="small"
                        sx={{
                          backgroundColor: `${colors.info}15`,
                          color: colors.info,
                          fontSize: '0.7rem',
                          height: 20,
                        }}
                      />
                      {/* Where they teach — on every card for an account with
                          several locations (one chip per location, so someone
                          at two branches shows both, and someone at one still
                          shows it). An older record with only `studio` counts
                          as that one location. Otherwise (super admin, single
                          location) as before: only someone at 2+ studios. */}
                      {(locationById.size > 1 || (Array.isArray(instructor.studios) && instructor.studios.length > 1)) &&
                        (instructor.studios?.length ? instructor.studios : instructor.studio ? [instructor.studio] : []).map((st: any) => {
                          const id = String(st?._id ?? st);
                          return (
                            <BranchChip
                              key={id}
                              location={locationById.get(id) ?? (st?.studioName ? { _id: id, studioName: st.studioName, branchName: st.branchName } as any : null)}
                            />
                          );
                        })}
                      {instructor.isDisabled && (
                        <Chip
                          label="Disabled"
                          size="small"
                          sx={{
                            backgroundColor: `${colors.textSecondary}20`,
                            color: colors.textSecondary,
                            fontSize: '0.7rem',
                            height: 20,
                            fontWeight: 600,
                          }}
                        />
                      )}
                    </Box>
                    <Box display="flex" flexDirection="column" gap={1}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Phone sx={{ fontSize: 16, color: colors.textSecondary }} />
                        <Typography variant="body2" color="text.secondary">
                          {instructor.phoneNumber}
                        </Typography>
                      </Box>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Class sx={{ fontSize: 16, color: colors.textSecondary }} />
                        <Typography variant="body2" color="text.secondary">
                          {/* Every studio they teach at, not just the first. */}
                          {(Array.isArray(instructor.studios) && instructor.studios.length
                            ? Array.from(
                                new Set(
                                  instructor.studios
                                    .map((st: any) => locationById.get(String(st?._id ?? st))?.studioName || st?.studioName)
                                    .filter(Boolean),
                                ),
                              ).join(', ')
                            : '') || instructor.studio?.studioName || 'N/A'}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      {/* Menu */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={handleEditInstructor}>
          <Edit sx={{ fontSize: 20, mr: 1 }} /> Edit
        </MenuItem>
        <MenuItem onClick={handleToggleInstructor} sx={{ color: 'error.main' }}>
          <Block sx={{ fontSize: 20, mr: 1 }} /> Revoke access
        </MenuItem>
      </Menu>

      {/* Create Dialog */}
      {createDialogOpen && (
        <InstructorFormDialog
          open={createDialogOpen}
          onClose={() => setCreateDialogOpen(false)}
          onSuccess={() => { setCreateDialogOpen(false); fetchInstructors(); }}
          studioId={createForStudio || studioId}
        />
      )}

      {/* Super admin: which studio is this instructor for? */}
      <Dialog open={pickStudioOpen} onClose={() => setPickStudioOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Create instructor</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Choose the studio. If it belongs to a brand with several locations, you can tick the
            others on the next screen.
          </Typography>
          <Autocomplete
            options={studioOptions}
            loading={studiosLoading}
            value={pickedStudio}
            onChange={(_, v) => setPickedStudio(v as any)}
            getOptionLabel={(o: any) => (o?.branchName && !String(o.studioName).includes(o.branchName) ? `${o.studioName} — ${o.branchName}` : o?.studioName) || ''}
            isOptionEqualToValue={(a: any, b: any) => a._id === b._id}
            renderOption={(props, o: any) => (
              <li {...props} key={o._id}>
                <Box>
                  <Typography variant="body2">{o.studioName}{o.branchName && !String(o.studioName).includes(o.branchName) ? ` — ${o.branchName}` : ''}</Typography>
                  {o.city && <Typography variant="caption" color="text.secondary">{o.city}</Typography>}
                </Box>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                id="create-instructor-studio"
                label="Studio"
                placeholder="Search studios"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {studiosLoading ? <CircularProgress size={16} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          {studiosError && <Alert severity="error" sx={{ mt: 2 }}>{studiosError}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setPickStudioOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!pickedStudio}
            onClick={() => {
              if (!pickedStudio) return;
              setCreateForStudio(pickedStudio._id);
              setPickStudioOpen(false);
              setCreateDialogOpen(true);
            }}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      {instructorToEdit && (
        <InstructorFormDialog
          open={editDialogOpen}
          onClose={() => { setEditDialogOpen(false); setInstructorToEdit(null); }}
          onSuccess={() => { setEditDialogOpen(false); setInstructorToEdit(null); fetchInstructors(); }}
          instructor={instructorToEdit}
          studioId={instructorToEdit.studio?._id || studioId}
        />
      )}

      <Snackbar
        open={!!notice}
        autoHideDuration={5000}
        onClose={() => setNotice(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setNotice(null)} sx={{ width: '100%' }}>{notice}</Alert>
      </Snackbar>

      {/* Reactivate confirmation */}
      <Dialog open={!!reactivateTarget} onClose={reactivating ? undefined : () => setReactivateTarget(null)} fullWidth maxWidth="xs">
        <DialogTitle>Reactivate instructor</DialogTitle>
        <DialogContent>
          <Typography>
            Reactivate <strong>{reactivateTarget?.name}</strong>
            {isSuperAdmin
              ? ' at every location they were removed from?'
              : ' at this location?'}{' '}
            They appear in the schedule lists again, and their login gets access back there.
          </Typography>
          {reactivateError && <Alert severity="error" sx={{ mt: 2 }}>{reactivateError}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setReactivateTarget(null)} disabled={reactivating}>Cancel</Button>
          <Button
            variant="contained"
            color="success"
            onClick={confirmReactivate}
            disabled={reactivating}
            startIcon={reactivating ? <CircularProgress size={16} color="inherit" /> : <CheckCircleOutline />}
          >
            {reactivating ? 'Reactivating…' : 'Reactivate'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Disable/Enable Confirmation Dialog */}
      <Dialog open={toggleDialogOpen} onClose={() => { setToggleDialogOpen(false); setFutureWork(null); setReplacementId(''); }}>
        <DialogTitle>Revoke access</DialogTitle>
        <DialogContent>
          <Typography>
            {isSuperAdmin ? (
              <>Revoke <strong>{instructorToToggle?.name}</strong>'s access at <b>every location</b> they teach at? Nothing is deleted: their history stays, and you can reactivate them from the <b>Deactivated</b> tab.</>
            ) : (
              <>Revoke <strong>{instructorToToggle?.name}</strong>'s access at <b>{revokeLocationName}</b>? They are taken off this location's schedule list and their login can no longer open it.{(instructorToToggle?.studios?.length ?? 0) > 1 ? ' They keep teaching at their other locations.' : ''} Nothing is deleted — reactivate them any time from the <b>Deactivated</b> tab.</>
            )}
          </Typography>

          {/* Only appears once the backend has refused: the instructor is still
              on the schedule, so removing them without a handover would leave
              their name on classes members can still book. */}
          {futureWork && (
            <InstructorHandoverFields
              futureWork={futureWork}
              replacementOptions={replacementOptions}
              replacementId={replacementId}
              onReplacementChange={setReplacementId}
              notifyMembers={notifyMembers}
              onNotifyMembersChange={setNotifyMembers}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setToggleDialogOpen(false); setFutureWork(null); setReplacementId(''); setNotifyMembers(false); }} disabled={isToggling}>Cancel</Button>
          <Button
            onClick={confirmToggleInstructor}
            color={instructorToToggle?.isDisabled ? 'success' : 'error'}
            variant="contained"
            disabled={isToggling || (!!futureWork && !replacementId)}
          >
            {isToggling ? <CircularProgress size={20} /> : instructorToToggle?.isDisabled ? 'Enable' : futureWork ? 'Reassign & disable' : 'Disable'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InstructorsPage;

